import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { DollarSign, TrendingUp, TrendingDown, Clock, X, Users, FileX, AlertCircle, Ban, Loader2, AlertTriangle } from 'lucide-react';
import { FinancialSummary, ClientUtilization, LTSSService } from '../../types/ltss';
import { formatCurrency } from '../../utils/timeUtils';
import { getEffectiveRates, calculateServiceValue } from '../Settings/ProcedureRates';

interface RevenueSummaryProps {
  financials: FinancialSummary;
  clientUtilization?: ClientUtilization[];
  services?: LTSSService[];
}

type LeakageCategory = 'pending' | 'notSubmitted' | 'voided' | 'adjustmentInProgress';

export function RevenueSummary({ financials, clientUtilization = [], services = [] }: RevenueSummaryProps) {
  const [showMissedRevenueModal, setShowMissedRevenueModal] = useState(false);
  const [showLeakageModal, setShowLeakageModal] = useState(false);
  const [selectedLeakageCategory, setSelectedLeakageCategory] = useState<LeakageCategory | null>(null);

  // Get clients with missed revenue (underutilized), sorted by missed revenue
  const clientsWithMissedRevenue = clientUtilization
    .filter(c => c.missedRevenue > 0)
    .sort((a, b) => b.missedRevenue - a.missedRevenue);

  // Get effective rates (derived from paid claims + user overrides)
  const effectiveRates = useMemo(() => getEffectiveRates(services), [services]);

  // Calculate leakage breakdown by claim status using accurate per-procedure-code rates
  const leakageBreakdown = useMemo(() => {
    const breakdown = {
      pending: { count: 0, units: 0, estimatedValue: 0, services: [] as LTSSService[], missingRates: 0 },
      notSubmitted: { count: 0, units: 0, estimatedValue: 0, services: [] as LTSSService[], missingRates: 0 },
      voided: { count: 0, units: 0, estimatedValue: 0, services: [] as LTSSService[], missingRates: 0 },
      adjustmentInProgress: { count: 0, units: 0, estimatedValue: 0, services: [] as LTSSService[], missingRates: 0 },
    };

    // Track procedure codes used for display
    const procedureCodesUsed = new Set<string>();
    let totalMissingRates = 0;

    services.forEach(s => {
      const units = s.unitsBilled || 0;
      const { value: estimatedValue, hasRate } = calculateServiceValue(s, effectiveRates);
      
      if (s.procedureCode) {
        procedureCodesUsed.add(s.procedureCode);
      }

      switch (s.claimHistory) {
        case 'Original Claim Submitted':
          breakdown.pending.count++;
          breakdown.pending.units += units;
          breakdown.pending.estimatedValue += estimatedValue;
          breakdown.pending.services.push(s);
          if (!hasRate) breakdown.pending.missingRates++;
          break;
        case 'Original Claim NotSubmitted':
          breakdown.notSubmitted.count++;
          breakdown.notSubmitted.units += units;
          breakdown.notSubmitted.estimatedValue += estimatedValue;
          breakdown.notSubmitted.services.push(s);
          if (!hasRate) breakdown.notSubmitted.missingRates++;
          break;
        case 'Void Claim Paid':
          breakdown.voided.count++;
          breakdown.voided.units += units;
          breakdown.voided.estimatedValue += estimatedValue;
          breakdown.voided.services.push(s);
          if (!hasRate) breakdown.voided.missingRates++;
          break;
        case 'Adjustment In Progress':
          breakdown.adjustmentInProgress.count++;
          breakdown.adjustmentInProgress.units += units;
          breakdown.adjustmentInProgress.estimatedValue += estimatedValue;
          breakdown.adjustmentInProgress.services.push(s);
          if (!hasRate) breakdown.adjustmentInProgress.missingRates++;
          break;
      }
      
      if (!hasRate && s.claimHistory && s.claimHistory !== 'Original Claim Paid' && s.claimHistory !== 'Adjustment Claim Paid') {
        totalMissingRates++;
      }
    });

    // Calculate total leakage from breakdown
    const totalLeakage = breakdown.pending.estimatedValue + 
                         breakdown.notSubmitted.estimatedValue + 
                         breakdown.voided.estimatedValue + 
                         breakdown.adjustmentInProgress.estimatedValue;

    return { breakdown, effectiveRates, procedureCodesUsed: Array.from(procedureCodesUsed), totalMissingRates, totalLeakage };
  }, [services, effectiveRates]);

  const cards = [
    {
      title: 'Total Revenue',
      value: formatCurrency(financials.totalAmountPaid),
      icon: DollarSign,
      color: 'text-green-400',
      bgColor: 'bg-green-500/10',
      clickable: false,
    },
    {
      title: 'Units Billed',
      value: financials.totalUnitsBilled.toLocaleString(),
      icon: TrendingUp,
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/10',
      clickable: false,
    },
    {
      title: 'Missed Revenue',
      value: formatCurrency(financials.totalMissedRevenue),
      subtitle: 'from unused hours',
      icon: Clock,
      color: financials.totalMissedRevenue > 0 ? 'text-orange-400' : 'text-green-400',
      bgColor: financials.totalMissedRevenue > 0 ? 'bg-orange-500/10' : 'bg-green-500/10',
      clickable: true,
      onClick: () => setShowMissedRevenueModal(true),
    },
    {
      title: 'Est. Leakage',
      value: formatCurrency(leakageBreakdown.totalLeakage),
      subtitle: leakageBreakdown.totalMissingRates > 0 ? `${leakageBreakdown.totalMissingRates} missing rates` : 'billing issues',
      icon: TrendingDown,
      color: leakageBreakdown.totalLeakage > 0 ? 'text-red-400' : 'text-green-400',
      bgColor: leakageBreakdown.totalLeakage > 0 ? 'bg-red-500/10' : 'bg-green-500/10',
      clickable: true,
      onClick: () => setShowLeakageModal(true),
    },
  ];

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((card) => (
          <div 
            key={card.title} 
            className={`card p-4 ${card.clickable ? 'cursor-pointer hover:bg-slate-700/50 transition-colors' : ''}`}
            onClick={card.clickable ? card.onClick : undefined}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">{card.title}</p>
                <p className={`text-xl font-bold ${card.color} mt-1`}>{card.value}</p>
                {card.subtitle && (
                  <p className="text-xs text-slate-500 mt-0.5">{card.subtitle}</p>
                )}
                {card.clickable && (
                  <p className="text-xs text-slate-500 mt-1">(click for details)</p>
                )}
              </div>
              <div className={`w-10 h-10 rounded-lg ${card.bgColor} flex items-center justify-center`}>
                <card.icon className={`w-5 h-5 ${card.color}`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Missed Revenue Drill-down Modal */}
      {showMissedRevenueModal && createPortal(
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4"
          style={{ zIndex: 9999 }}
          onClick={() => setShowMissedRevenueModal(false)}
        >
          <div 
            className="bg-slate-800 rounded-xl p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-slate-700 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-white">Missed Revenue Breakdown</h3>
                <p className="text-sm text-slate-400">Revenue lost from unused authorized hours</p>
              </div>
              <button
                onClick={() => setShowMissedRevenueModal(false)}
                className="p-1.5 text-slate-400 hover:text-slate-200 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-orange-400" />
                <span className="text-sm text-slate-400">Total Missed Revenue:</span>
                <span className="text-xl font-bold text-orange-400">{formatCurrency(financials.totalMissedRevenue)}</span>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                This represents potential revenue from authorized hours that were not utilized by clients.
              </p>
            </div>

            <div className="flex items-center gap-2 mb-3">
              <Users className="w-4 h-4 text-slate-400" />
              <h4 className="text-sm font-medium text-slate-300">
                Clients with Unused Hours ({clientsWithMissedRevenue.length})
              </h4>
            </div>

            <div className="space-y-2">
              {clientsWithMissedRevenue.map((c, idx) => {
                const unusedHours = c.authorizedHours - c.usedHours;
                return (
                  <div key={idx} className="bg-slate-700/50 rounded-lg p-3 border border-slate-600">
                    <div className="flex items-center justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-200">{c.clientName}</p>
                        <div className="flex items-center gap-4 text-xs text-slate-400 mt-1">
                          <span>{c.week}</span>
                          <span className="text-blue-400">{c.program}</span>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-slate-400 mt-1">
                          <span>Authorized: {c.authorizedHours.toFixed(1)} hrs</span>
                          <span>Used: {c.usedHours.toFixed(1)} hrs</span>
                          <span className="text-orange-400 font-medium">
                            Unused: {unusedHours.toFixed(1)} hrs
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="text-orange-400 font-medium whitespace-nowrap">
                          {formatCurrency(c.missedRevenue)}
                        </span>
                        <p className="text-xs text-slate-500">{c.utilizationRate}% utilized</p>
                      </div>
                    </div>
                  </div>
                );
              })}
              {clientsWithMissedRevenue.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-4">
                  All clients are fully utilizing their authorized hours!
                </p>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Leakage Breakdown Modal */}
      {showLeakageModal && createPortal(
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4"
          style={{ zIndex: 9999 }}
          onClick={() => setShowLeakageModal(false)}
        >
          <div 
            className="bg-slate-800 rounded-xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-slate-700 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-white">Billing Leakage Breakdown</h3>
                <p className="text-sm text-slate-400">Revenue at risk from billing issues</p>
              </div>
              <button
                onClick={() => setShowLeakageModal(false)}
                className="p-1.5 text-slate-400 hover:text-slate-200 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2">
                <TrendingDown className="w-5 h-5 text-red-400" />
                <span className="text-sm text-slate-400">Total Estimated Leakage:</span>
                <span className="text-xl font-bold text-red-400">{formatCurrency(leakageBreakdown.totalLeakage)}</span>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Calculated using {Object.keys(leakageBreakdown.effectiveRates).length} procedure code rates derived from paid claims
              </p>
              {leakageBreakdown.totalMissingRates > 0 && (
                <div className="flex items-center gap-2 mt-2 text-yellow-400">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="text-xs">{leakageBreakdown.totalMissingRates} services missing rate data - go to Settings to add rates</span>
                </div>
              )}
            </div>

            {/* Breakdown Cards - Clickable for drill-down */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* Pending Claims */}
              <button 
                type="button"
                className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 cursor-pointer hover:bg-yellow-500/20 transition-colors text-left w-full"
                onClick={(e) => { e.stopPropagation(); setSelectedLeakageCategory('pending'); }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Loader2 className="w-5 h-5 text-yellow-400" />
                  <span className="font-medium text-yellow-400">Claims Pending</span>
                </div>
                <p className="text-2xl font-bold text-white">{formatCurrency(leakageBreakdown.breakdown.pending.estimatedValue)}</p>
                <p className="text-sm text-slate-400 mt-1">
                  {leakageBreakdown.breakdown.pending.count} services • {leakageBreakdown.breakdown.pending.units.toLocaleString()} units
                </p>
                <p className="text-xs text-slate-500 mt-2">Submitted but awaiting payment</p>
                {leakageBreakdown.breakdown.pending.count > 0 && (
                  <p className="text-xs text-yellow-400 mt-2">(click to view details)</p>
                )}
              </button>

              {/* Not Submitted */}
              <button 
                type="button"
                className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-4 cursor-pointer hover:bg-orange-500/20 transition-colors text-left w-full"
                onClick={(e) => { e.stopPropagation(); setSelectedLeakageCategory('notSubmitted'); }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <FileX className="w-5 h-5 text-orange-400" />
                  <span className="font-medium text-orange-400">Not Submitted</span>
                </div>
                <p className="text-2xl font-bold text-white">{formatCurrency(leakageBreakdown.breakdown.notSubmitted.estimatedValue)}</p>
                <p className="text-sm text-slate-400 mt-1">
                  {leakageBreakdown.breakdown.notSubmitted.count} services • {leakageBreakdown.breakdown.notSubmitted.units.toLocaleString()} units
                </p>
                <p className="text-xs text-slate-500 mt-2">Claims not yet submitted for billing</p>
                {leakageBreakdown.breakdown.notSubmitted.count > 0 && (
                  <p className="text-xs text-orange-400 mt-2">(click to view details)</p>
                )}
              </button>

              {/* Voided */}
              <button 
                type="button"
                className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 cursor-pointer hover:bg-red-500/20 transition-colors text-left w-full"
                onClick={(e) => { e.stopPropagation(); setSelectedLeakageCategory('voided'); }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Ban className="w-5 h-5 text-red-400" />
                  <span className="font-medium text-red-400">Voided Claims</span>
                </div>
                <p className="text-2xl font-bold text-white">{formatCurrency(leakageBreakdown.breakdown.voided.estimatedValue)}</p>
                <p className="text-sm text-slate-400 mt-1">
                  {leakageBreakdown.breakdown.voided.count} services • {leakageBreakdown.breakdown.voided.units.toLocaleString()} units
                </p>
                <p className="text-xs text-slate-500 mt-2">Claims that were voided/cancelled</p>
                {leakageBreakdown.breakdown.voided.count > 0 && (
                  <p className="text-xs text-red-400 mt-2">(click to view details)</p>
                )}
              </button>

              {/* Adjustment In Progress */}
              <button 
                type="button"
                className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-4 cursor-pointer hover:bg-purple-500/20 transition-colors text-left w-full"
                onClick={(e) => { e.stopPropagation(); setSelectedLeakageCategory('adjustmentInProgress'); }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="w-5 h-5 text-purple-400" />
                  <span className="font-medium text-purple-400">Adjustments Pending</span>
                </div>
                <p className="text-2xl font-bold text-white">{formatCurrency(leakageBreakdown.breakdown.adjustmentInProgress.estimatedValue)}</p>
                <p className="text-sm text-slate-400 mt-1">
                  {leakageBreakdown.breakdown.adjustmentInProgress.count} services • {leakageBreakdown.breakdown.adjustmentInProgress.units.toLocaleString()} units
                </p>
                <p className="text-xs text-slate-500 mt-2">Claims with adjustments in progress</p>
                {leakageBreakdown.breakdown.adjustmentInProgress.count > 0 && (
                  <p className="text-xs text-purple-400 mt-2">(click to view details)</p>
                )}
              </button>
            </div>

            {/* Service Details Drill-down */}
            {selectedLeakageCategory && (
              <div className="bg-slate-700/50 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-slate-200">
                    {selectedLeakageCategory === 'pending' && 'Pending Claims Details'}
                    {selectedLeakageCategory === 'notSubmitted' && 'Not Submitted Claims Details'}
                    {selectedLeakageCategory === 'voided' && 'Voided Claims Details'}
                    {selectedLeakageCategory === 'adjustmentInProgress' && 'Adjustment In Progress Details'}
                  </h4>
                  <button
                    onClick={() => setSelectedLeakageCategory(null)}
                    className="text-xs text-slate-400 hover:text-slate-200"
                  >
                    Close
                  </button>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-slate-700">
                      <tr>
                        <th className="text-left px-2 py-1 text-slate-400">Date</th>
                        <th className="text-left px-2 py-1 text-slate-400">Client</th>
                        <th className="text-left px-2 py-1 text-slate-400">Staff</th>
                        <th className="text-left px-2 py-1 text-slate-400">Code</th>
                        <th className="text-right px-2 py-1 text-slate-400">Units</th>
                        <th className="text-right px-2 py-1 text-slate-400">Est. Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leakageBreakdown.breakdown[selectedLeakageCategory].services
                        .sort((a, b) => {
                          const dateA = a.serviceDate ? new Date(a.serviceDate).getTime() : 0;
                          const dateB = b.serviceDate ? new Date(b.serviceDate).getTime() : 0;
                          return dateB - dateA;
                        })
                        .map((s, idx) => {
                          const { value, hasRate } = calculateServiceValue(s, leakageBreakdown.effectiveRates);
                          return (
                            <tr key={idx} className="border-t border-slate-600">
                              <td className="px-2 py-1.5 text-slate-300">
                                {s.serviceDate ? new Date(s.serviceDate).toLocaleDateString() : '—'}
                              </td>
                              <td className="px-2 py-1.5 text-slate-300 truncate max-w-[120px]" title={s.clientName}>
                                {s.clientName}
                              </td>
                              <td className="px-2 py-1.5 text-slate-300 truncate max-w-[100px]" title={s.staffName}>
                                {s.staffName}
                              </td>
                              <td className="px-2 py-1.5">
                                <code className="text-blue-400 bg-slate-800 px-1 rounded text-xs">{s.procedureCode || '—'}</code>
                              </td>
                              <td className="px-2 py-1.5 text-right text-slate-300">{s.unitsBilled || 0}</td>
                              <td className={`px-2 py-1.5 text-right ${hasRate ? 'text-slate-300' : 'text-yellow-400'}`}>
                                {hasRate ? formatCurrency(value) : 'No rate'}
                              </td>
                            </tr>
                          );
                        })}
                      {leakageBreakdown.breakdown[selectedLeakageCategory].services.length === 0 && (
                        <tr>
                          <td colSpan={6} className="px-2 py-4 text-center text-slate-500">
                            No services in this category
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Action Items */}
            <div className="bg-slate-700/50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-slate-200 mb-3">Recommended Actions</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                {leakageBreakdown.breakdown.notSubmitted.count > 0 && (
                  <li className="flex items-start gap-2">
                    <span className="text-orange-400">•</span>
                    <span>Review and submit {leakageBreakdown.breakdown.notSubmitted.count} unsubmitted claims worth {formatCurrency(leakageBreakdown.breakdown.notSubmitted.estimatedValue)}</span>
                  </li>
                )}
                {leakageBreakdown.breakdown.pending.count > 0 && (
                  <li className="flex items-start gap-2">
                    <span className="text-yellow-400">•</span>
                    <span>Follow up on {leakageBreakdown.breakdown.pending.count} pending claims worth {formatCurrency(leakageBreakdown.breakdown.pending.estimatedValue)}</span>
                  </li>
                )}
                {leakageBreakdown.breakdown.voided.count > 0 && (
                  <li className="flex items-start gap-2">
                    <span className="text-red-400">•</span>
                    <span>Investigate {leakageBreakdown.breakdown.voided.count} voided claims to prevent future voids</span>
                  </li>
                )}
                {leakageBreakdown.breakdown.adjustmentInProgress.count > 0 && (
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>Monitor {leakageBreakdown.breakdown.adjustmentInProgress.count} claims with pending adjustments</span>
                  </li>
                )}
                {leakageBreakdown.breakdown.notSubmitted.count === 0 && 
                 leakageBreakdown.breakdown.pending.count === 0 && 
                 leakageBreakdown.breakdown.voided.count === 0 &&
                 leakageBreakdown.breakdown.adjustmentInProgress.count === 0 && (
                  <li className="text-green-400">All claims are processed! No immediate action needed.</li>
                )}
              </ul>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
